import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { isDemoMode, supabase } from './supabase';
import { getAccess, getActiveTenantId, setActiveTenantId, type AccessState, type AccessTenant } from './api';

type AuthContextValue = {
  session: Session | null;
  loading: boolean;
  accessLoading: boolean;
  demoMode: boolean;
  access: AccessState | null;
  activeTenant: AccessTenant | null;
  setActiveTenant: (tenantId: string) => void;
  refreshAccess: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

type AccessRow = {
  profile_id: string;
  email: string;
  display_name: string | null;
  super_admin: boolean;
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  tenant_role: AccessTenant['role'];
  support_context: boolean;
};

async function loadOwnAccessFallback(_userId: string): Promise<AccessState> {
  if (!supabase) throw new Error('Supabase client is unavailable.');
  const { data, error } = await supabase.rpc('get_my_access');
  if (error) throw error;
  const rows = (data ?? []) as AccessRow[];
  if (!rows.length) return { profile: null, super_admin: false, tenants: [] };
  const [first] = rows;
  return {
    profile: { id: first.profile_id, email: first.email, display_name: first.display_name, super_admin: Boolean(first.super_admin) },
    super_admin: Boolean(first.super_admin),
    tenants: rows.map((row) => ({ id: row.tenant_id, name: row.tenant_name, slug: row.tenant_slug, role: row.tenant_role, support_context: row.support_context })),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(!isDemoMode);
  const [accessLoading, setAccessLoading] = useState(!isDemoMode);
  const [access, setAccess] = useState<AccessState | null>(null);
  const [activeTenantId, setActiveTenantIdState] = useState<string | null>(getActiveTenantId());

  const refreshAccess = async () => {
    if (isDemoMode || !supabase) { setAccess(null); setAccessLoading(false); return; }
    setAccessLoading(true);
    try {
      let nextAccess: AccessState;
      try {
        nextAccess = await getAccess();
        if (nextAccess.tenants.length === 0) {
          const { data: userData, error: userError } = await supabase.auth.getUser();
          if (userError || !userData.user) throw userError ?? new Error('Your sign-in session has expired.');
          const fallbackAccess = await loadOwnAccessFallback(userData.user.id);
          if (fallbackAccess.tenants.length > 0 || fallbackAccess.super_admin) nextAccess = fallbackAccess;
        }
      } catch {
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError || !userData.user) throw userError ?? new Error('Your sign-in session has expired.');
        nextAccess = await loadOwnAccessFallback(userData.user.id);
      }
      setAccess(nextAccess);
      const storedTenantId = getActiveTenantId();
      const usableTenant = nextAccess.tenants.find((tenant) => tenant.id === storedTenantId) ?? nextAccess.tenants[0] ?? null;
      setActiveTenantIdState(usableTenant?.id ?? null);
      setActiveTenantId(usableTenant?.id ?? null);
    } finally {
      setAccessLoading(false);
    }
  };

  useEffect(() => {
    if (!supabase) return;
    let mounted = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
      if (data.session) void refreshAccess(); else { setAccess(null); setAccessLoading(false); }
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setLoading(false);
      if (nextSession) void refreshAccess(); else { setAccess(null); setAccessLoading(false); }
    });
    return () => { mounted = false; listener.subscription.unsubscribe(); };
  }, []);

  const setActiveTenant = (tenantId: string) => {
    const selected = access?.tenants.find((tenant) => tenant.id === tenantId);
    if (!selected) return;
    setActiveTenantId(tenantId);
    setActiveTenantIdState(tenantId);
  };

  const activeTenant = access?.tenants.find((tenant) => tenant.id === activeTenantId) ?? null;
  const value = useMemo<AuthContextValue>(() => ({
    session,
    loading,
    accessLoading,
    demoMode: isDemoMode,
    access,
    activeTenant,
    setActiveTenant,
    refreshAccess,
    signOut: async () => { await supabase?.auth.signOut(); setActiveTenantId(null); setActiveTenantIdState(null); },
  }), [access, accessLoading, activeTenant, loading, session]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider');
  return value;
}
